import { Modal, Upload, Button, Image, Divider, Space, Typography, message as antMessage } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { OrganizationForm } from "./OrganizationForm";
import { OrgStorageSection } from "./OrgStorageSection";
import { servicesUrlConfig } from "shared/config/setting";

const { Text } = Typography;
const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='11'%3ENo logo%3C/text%3E%3C/svg%3E";

/**
 * Reusable Modal for Adding an Organization
 */
export const AddOrganizationModal = ({
  visible,
  organization,
  onOk,
  onCancel,
  onChange,
  translate = (key) => key,
}) => {
  return (
    <Modal
      title={translate("organizations.addOrganization")}
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
    >
      <OrganizationForm
        organization={organization}
        onChange={onChange}
        translate={translate}
      />
    </Modal>
  );
};

/**
 * Reusable Modal for Editing an Organization
 */
export const EditOrganizationModal = ({
  visible,
  organization,
  onOk,
  onCancel,
  onChange,
  onLogoUpload,
  onLogoDelete,
  canManageLogo = false,
  canManageStorage = false,
  showStorage = true,
  translate = (key) => key,
}) => {
  const getStaticUrl = servicesUrlConfig.ROBOTS_API.getStaticUrl;

  const makeUploadProps = (logoField, uploadFn, deleteFn) => ({
    accept: ".png,.jpg,.jpeg,.webp",
    showUploadList: false,
    beforeUpload: async (file) => {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        antMessage.error("Only PNG, JPG, or WEBP images are allowed.");
        return Upload.LIST_IGNORE;
      }
      if (file.size > 5 * 1024 * 1024) {
        antMessage.error("File must be smaller than 5 MB.");
        return Upload.LIST_IGNORE;
      }
      await uploadFn(file);
      return false;
    },
  });

  return (
    <Modal
      title={translate("organizations.editOrganization")}
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
    >
      <OrganizationForm
        organization={organization}
        onChange={onChange}
        translate={translate}
      />

      {canManageLogo && onLogoUpload && onLogoDelete && (
        <>
          <Divider />
          <Space direction="vertical" style={{ width: "100%" }}>
            {/* Full Logo */}
            <div>
              <Text strong>Full Logo</Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
                Shown in sidebar when expanded. PNG/JPG/WEBP, max 5 MB.
              </Text>
              <Space align="center">
                <Image
                  src={organization.logo_path ? getStaticUrl(organization.logo_path) : PLACEHOLDER}
                  width={80}
                  height={80}
                  style={{ objectFit: "contain", border: "1px solid #f0f0f0", borderRadius: 4 }}
                  preview={!!organization.logo_path}
                  fallback={PLACEHOLDER}
                />
                <Space direction="vertical" size="small">
                  <Upload {...makeUploadProps("logo_path", (f) => onLogoUpload(f, "logo"), (f) => onLogoDelete("logo"))}>
                    <Button icon={<UploadOutlined />} size="small">Upload</Button>
                  </Upload>
                  {organization.logo_path && (
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onLogoDelete("logo")}
                    >
                      Reset
                    </Button>
                  )}
                </Space>
              </Space>
            </div>

            {/* Compact Logo */}
            <div>
              <Text strong>Compact Logo</Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
                Shown in sidebar when collapsed. PNG/JPG/WEBP, max 5 MB.
              </Text>
              <Space align="center">
                <Image
                  src={organization.logo_compact_path ? getStaticUrl(organization.logo_compact_path) : PLACEHOLDER}
                  width={80}
                  height={80}
                  style={{ objectFit: "contain", border: "1px solid #f0f0f0", borderRadius: 4 }}
                  preview={!!organization.logo_compact_path}
                  fallback={PLACEHOLDER}
                />
                <Space direction="vertical" size="small">
                  <Upload {...makeUploadProps("logo_compact_path", (f) => onLogoUpload(f, "logo-compact"), (f) => onLogoDelete("logo-compact"))}>
                    <Button icon={<UploadOutlined />} size="small">Upload</Button>
                  </Upload>
                  {organization.logo_compact_path && (
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onLogoDelete("logo-compact")}
                    >
                      Reset
                    </Button>
                  )}
                </Space>
              </Space>
            </div>
          </Space>
        </>
      )}

      {/* Storage configuration (raasdata-service). Gated on `visible` so the
          section remounts — and reloads the config — every time the modal
          opens (AntD keeps modal children mounted after the first open).
          Callers that surface storage elsewhere pass showStorage={false}. */}
      {showStorage && visible && organization?.org_id && (
        <>
          <Divider />
          <OrgStorageSection
            orgId={organization.org_id}
            canManage={canManageStorage}
            translate={translate}
          />
        </>
      )}
    </Modal>
  );
};
